import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import {
  nextTheme as computeNextTheme,
  readStoredTheme,
  resolveInitialTheme,
  storeTheme,
  type Theme
} from "./theme-storage.js";

export interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function detectInitialTheme(): Theme {
  if (typeof window === "undefined") {
    return resolveInitialTheme(null, false);
  }
  const stored = readStoredTheme(window.localStorage);
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return resolveInitialTheme(stored, prefersDark);
}

/**
 * Tema claro/escuro (Design.md §3.1): aplica `data-theme` na raiz e persiste a
 * escolha. A lógica pura vive em `theme-storage.ts` (testável sem DOM).
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [theme, setThemeState] = useState<Theme>(detectInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    storeTheme(window.localStorage, theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => computeNextTheme(current));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, toggleTheme, setTheme }),
    [theme, toggleTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error("useTheme deve ser usado dentro de <ThemeProvider>.");
  }
  return value;
}
